import { FC, memo, ReactElement, useCallback } from 'react';

import { AppMessage } from '@/common/components';
import { useAppPagination } from '@/common/hooks';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  useDeployDomainsMutation,
  useDetachConfigDomainsMutation,
  useGetConfigDomainsQuery,
  useShutdownDomainsMutation,
  useUpdateDomainConfigurationStatus,
  useUpdateDomainDeployStatus,
  useUpdateProxyServerStatuses
} from '@/modules/Domains';

import { DomainConfigOverviewView } from './DomainConfigOverviewView';

interface Props {
  domainId: string;
}

export const DomainConfigOverviewContainer: FC<Props> = memo(({ domainId }) => {
  const { pageParam } = useAppPagination();
  const appMessageApi = AppMessage.useControls();

  const {
    data: domainDeploymentInfoData,
    error: domainDeploymentInfoError,
    isLoading: isDomainDeploymentInfoLoading,
    isFetching: isDomainDeploymentInfoFetching,
    isError: isDomainDeploymentInfoError,
    isSuccess: isDomainDeploymentInfoSuccess,
    refetch: refetchDomainDeploymentInfo
  } = useGetConfigDomainsQuery(domainId)({
    enabled: !!domainId
  });

  useUpdateDomainDeployStatus({
    domainId,
    pageParam: { page: pageParam },
    domainName: domainDeploymentInfoData?.name ?? null,
  });

  useUpdateProxyServerStatuses({ domainId });

  useUpdateDomainConfigurationStatus({
    domainId,
    pageParam: { page: pageParam }
  });

  const { mutate: deployDomainMutation, isLoading: isDeployDomainLoading } = useDeployDomainsMutation(domainId)({
    onSuccess: async () => {
      await refetchDomainDeploymentInfo();
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const { mutate: shutdownDomainMutation, isLoading: isShutdownDomainLoading } = useShutdownDomainsMutation(domainId)({
    onSuccess: async () => {
      await refetchDomainDeploymentInfo();
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const { mutate: detachConfigMutation, isLoading: isDetachConfigLoading } = useDetachConfigDomainsMutation(domainId)({
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const handleDeployDomain = useCallback((): void => {
    if (isDomainDeploymentInfoSuccess) {
      if (domainDeploymentInfoData.isDeployed) {
        shutdownDomainMutation();
      } else {
        deployDomainMutation();
      }
    }
  }, [isDomainDeploymentInfoSuccess, domainDeploymentInfoData?.isDeployed]);

  const handleDetachDeploymentConfig = useCallback((): void => {
    detachConfigMutation();
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isDomainDeploymentInfoLoading }
      isError={ isDomainDeploymentInfoError }
      error={ domainDeploymentInfoError }
      data={ domainDeploymentInfoData }
    >
      { (domainDeploymentInfo): ReactElement =>
        <DomainConfigOverviewView
          domainId={ domainId }
          domainDeploymentInfo={ domainDeploymentInfo }
          isDomainDeploymentInfoLoading={ isDomainDeploymentInfoFetching }
          isDeployDomainLoading={ isDeployDomainLoading }
          isShutdownDomainLoading={ isShutdownDomainLoading }
          isDetachConfigLoading={ isDetachConfigLoading }
          handleDeployDomain={ handleDeployDomain }
          handleDetachDeploymentConfig={ handleDetachDeploymentConfig }
        />
      }
    </FallbackedLayout>
  );
});
